// ─── Telegram Bot Client ────────────────────────────────────────
// Thin client wrapper around the `telegram-bot` edge function, plus typed
// workflow builders used to create multi-step order proposals.
//
// A proposal is a small guided conversation: the bot sends the first step with
// buttons, the user answers, the engine advances to the next step, and so on
// until the workflow finishes — optionally executing via ctrader-gateway.

import { supabase } from '@/lib/supabase';

// ─── Types ──────────────────────────────────────────────────────

export type ProposalStepInput = 'choice' | 'text' | 'confirm';
export type ProposalValueType = 'text' | 'number' | 'lots';
export type ProposalFinalAction = 'place_order' | 'close_position' | 'amend_sltp' | 'none';

export interface TelegramProposalOption {
  label: string;
  value: string;
}

export interface TelegramProposalStep {
  /** Short unique id within the workflow (used in logs, not in callback data). */
  id: string;
  /** Question shown to the user. */
  prompt: string;
  /** How the user answers: tap a button, type text, or confirm/cancel. */
  input: ProposalStepInput;
  /** Answer key stored in proposal.answers (e.g. trade_side, volume). */
  field?: string;
  /** How the raw value is coerced before storage. */
  value_type?: ProposalValueType;
  /** Options for choice steps (ignored for text/confirm). */
  options?: TelegramProposalOption[];
}

export interface TelegramProposalWorkflow {
  steps: TelegramProposalStep[];
  /** What to run once every step is answered. Omit / 'none' = record only. */
  final_action?: ProposalFinalAction;
}

export interface CreateProposalPayload {
  telegram_account_id?: number;
  chat_id?: string;
  account_id?: string | number;
  symbol_id?: number;
  symbol?: string;
  action?: string;
  trade_side?: string;
  volume?: number;
  stop_loss?: number;
  take_profit?: number;
  position_id?: number;
  workflow: TelegramProposalWorkflow;
  signal_context?: Record<string, unknown>;
  expires_minutes?: number;
}

export interface CreateProposalResult {
  success: boolean;
  error?: string;
  id?: string;
  code?: string;
  chat_id?: string;
}

export interface WebhookInfoResult {
  success: boolean;
  error?: string;
  account?: {
    id: number;
    name: string;
    chat_id: string;
    is_active: boolean;
  } | null;
  config?: {
    telegram_account_id: number;
    status: string;
    webhook_url: string | null;
    error_message: string | null;
    configured_at: string | null;
  } | null;
  info?: {
    url?: string;
    pending_update_count?: number;
    last_error_message?: string;
  } | null;
}

/** Result of one bot's webhook health check (auto re-register when unhealthy). */
export interface WebhookHealth {
  accountId: number;
  /** false when a problem was detected (even if the heal then failed). */
  healthy: boolean;
  /** true when the webhook was successfully re-registered this check. */
  healed: boolean;
  /** true when the check was skipped (still within the throttle window). */
  skipped: boolean;
  /** Human-readable reasons the bot was judged unhealthy (empty when healthy). */
  reasons: string[];
  pending: number;
  lastError: string | null;
  url: string | null;
  error?: string;
}

export interface WebhookHealthResult {
  success: boolean;
  error?: string;
  /** Present when a single account was checked. */
  result?: WebhookHealth;
  /** Present when all active accounts were checked. */
  results?: WebhookHealth[];
}

export interface SendRiskBlockPayload {
  /** Target Telegram account (optional — falls back to the first active one). */
  telegram_account_id?: number;
  /** Override chat id (optional — falls back to the account's chat_id). */
  chat_id?: string;
  /** Symbol that was blocked, e.g. "EURUSD". */
  symbol: string;
  /** Risk rule key that blocked the order: spread | maxOrders | singleSymbol | afterHours | notReady. */
  rule: string;
  /** Human-readable reason for the block. */
  reason: string;
}

export interface SendRiskBlockResult {
  success: boolean;
  error?: string;
  chat_id?: string;
}

export interface OrderActionResult {
  success: boolean;
  /** executed | failed | blocked | already_processed (server-reported outcome). */
  status?: string;
  error?: string;
}

/** KN1 — trigger the trend-candle "khởi tạo" conversation for a symbol. */
export interface SendKn1Payload {
  /** Target Telegram account (optional — falls back to the first active one). */
  telegram_account_id?: number;
  /** Override chat id (optional — falls back to the account's chat_id). */
  chat_id?: string;
  /** Symbol name, e.g. "EURUSD". */
  symbol: string;
  /** cTrader symbol id (optional, for the later order). */
  symbol_id?: number | null;
  /** Candle direction → Buy (BULL) or Sell (BEAR). */
  direction: 'BULL' | 'BEAR';
  /** task1 — the A0 candle body (pips) that triggered the alert (shown in the message). */
  body_pips?: number | null;
  /** task1 — the configured body threshold (pips) that was applied. */
  threshold_pips?: number | null;
}

export interface SendKn1Result {
  success: boolean;
  error?: string;
  /** Set when an active KN1 for the same symbol already exists (deduped). */
  skipped?: string;
  chat_id?: string;
}

/** KN1 — the browser tick that re-sends due "nhắc lại" init messages. */
export interface Kn1TickResult {
  success: boolean;
  error?: string;
  /** Number of KN1 init messages re-sent this tick. */
  resent?: number;
}

/** KN2 — trigger the "nến cắt TMA50" (khởi tạo 2) conversation for a symbol. */
export interface SendKn2Payload {
  /** Target Telegram account (optional — falls back to the first active one). */
  telegram_account_id?: number;
  /** Override chat id (optional — falls back to the account's chat_id). */
  chat_id?: string;
  /** Symbol name, e.g. "EURUSD". */
  symbol: string;
  /** cTrader symbol id (optional, for the later order). */
  symbol_id?: number | null;
  /** Cross direction of the candle through TMA50. */
  cross_dir: 'UP' | 'DOWN';
}

export interface SendKn2Result {
  success: boolean;
  error?: string;
  /** Set when an active KN2 for the same symbol already exists (deduped). */
  skipped?: string;
  chat_id?: string;
}

/** KN3 — trigger the "SMA50 và TMA50 Tụ Tách" (khởi tạo 3) conversation for a symbol. */
export interface SendKn3Payload {
  /** Target Telegram account (optional — falls back to the first active one). */
  telegram_account_id?: number;
  /** Override chat id (optional — falls back to the account's chat_id). */
  chat_id?: string;
  /** Symbol name, e.g. "EURUSD". */
  symbol: string;
  /** cTrader symbol id (optional, for the later order). */
  symbol_id?: number | null;
  /** task3 — the |SMA50 − TMA50| distance (in pips) that triggered KN3. */
  gap_pips?: number | null;
}

export interface SendKn3Result {
  success: boolean;
  error?: string;
  /** Set when an active KN3 for the same symbol already exists (deduped). */
  skipped?: string;
  chat_id?: string;
}

/** task7 — fire (notify) one installed alert; the edge claims it atomically. */
export interface FireAlertResult {
  success: boolean;
  error?: string;
  /** true when another caller already claimed/fired this alert (deduped). */
  already?: boolean;
  chat_id?: string;
}

// ─── Low-level invoke ───────────────────────────────────────────

async function invoke<T>(action: string, payload: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('telegram-bot', {
    body: { action, ...payload },
  });
  if (error) return { success: false, error: error.message } as unknown as T;
  return (data ?? {}) as T;
}

// ─── Public API ─────────────────────────────────────────────────

export const telegramBot = {
  /** Register the webhook so Telegram forwards updates to our engine. */
  setWebhook: (telegramAccountId: number) =>
    invoke<{ success: boolean; url?: string; error?: string }>('set-webhook', {
      telegram_account_id: telegramAccountId,
    }),

  /** Remove the webhook (bot stops forwarding). */
  deleteWebhook: (telegramAccountId: number) =>
    invoke<{ success: boolean; error?: string }>('delete-webhook', {
      telegram_account_id: telegramAccountId,
    }),

  /** Read current webhook status + Telegram's own view of it. */
  webhookInfo: (telegramAccountId: number) =>
    invoke<WebhookInfoResult>('webhook-info', { telegram_account_id: telegramAccountId }),

  /**
   * Health-check the webhook and AUTO re-register it when it looks broken
   * (recent `last_error`, a growing `pending_update_count`, or a URL mismatch).
   * Pass an account id to check one bot, or omit it to check every active bot.
   * `force` bypasses the server-side throttle (the automatic heartbeat keeps it).
   */
  webhookHealth: (telegramAccountId?: number, force?: boolean) =>
    invoke<WebhookHealthResult>('webhook-health', {
      telegram_account_id: telegramAccountId,
      force: force === true,
    }),

  /** Create a multi-step proposal and push the first step to Telegram. */
  createProposal: (payload: CreateProposalPayload) =>
    invoke<CreateProposalResult>('create-proposal', payload as unknown as Record<string, unknown>),

  /**
   * task5 — push a risk-block alert with inline buttons:
   *   [🔍 Vì sao bị chặn?]   [💡 Nên làm gì?]
   * The buttons carry a stateless callback (rk|why|rule|symbol) so pressing
   * them never needs a stored record.
   */
  sendRiskBlock: (payload: SendRiskBlockPayload) =>
    invoke<SendRiskBlockResult>('send-risk-block', payload as unknown as Record<string, unknown>),

  /**
   * task6 — the risk gate PASSED: atomically claim the pending order request
   * and fire the MARKET order on cTrader (server-side, via ctrader-gateway).
   */
  executeOrder: (proposalId: string) =>
    invoke<OrderActionResult>('execute-order', { proposal_id: proposalId }),

  /**
   * task6 — the risk gate BLOCKED the order: mark the request as blocked and
   * send the same interactive Telegram alert task5 uses (why / what to do).
   */
  blockOrder: (proposalId: string, rule: string, reason: string) =>
    invoke<OrderActionResult>('block-order', { proposal_id: proposalId, rule, reason }),

  /**
   * KN1 — send the trend-candle "khởi tạo" notification with [Test] /
   * [Vào Lệnh] / [Bỏ qua] buttons. Triggered by task1's big-candle event.
   */
  sendKn1: (payload: SendKn1Payload) =>
    invoke<SendKn1Result>('send-kn1', payload as unknown as Record<string, unknown>),

  /**
   * KN1 — the background tick: re-send every due "nhắc lại" init message.
   * Called periodically by the browser KN1 controller.
   */
  kn1Tick: () => invoke<Kn1TickResult>('kn1-tick', {}),

  /**
   * KN2 — send the "nến cắt TMA50" (khởi tạo 2) notification with [Test] /
   * [Vào Lệnh] buttons. Triggered by task4's TMA50-cross event.
   */
  sendKn2: (payload: SendKn2Payload) =>
    invoke<SendKn2Result>('send-kn2', payload as unknown as Record<string, unknown>),

  /**
   * KN3 — send the "SMA50 và TMA50 Tụ Tách" (khởi tạo 3) notification with
   * [Test] / [Vào Lệnh] buttons. Triggered by task3's ST50-convergence event.
   */
  sendKn3: (payload: SendKn3Payload) =>
    invoke<SendKn3Result>('send-kn3', payload as unknown as Record<string, unknown>),

  /**
   * Pending order ("Vào lệnh chờ") — the browser validated the entered price
   * against the live market: promote pending_new → pending_armed (monitored).
   */
  armPendingOrder: (proposalId: string) =>
    invoke<OrderActionResult>('arm-pending-order', { proposal_id: proposalId }),

  /** Pending order — the entered price is invalid vs the live market (reject + alert). */
  rejectPendingOrder: (proposalId: string, reason: string) =>
    invoke<OrderActionResult>('reject-pending-order', { proposal_id: proposalId, reason }),

  /** Pending order — the user deleted it from the dashboard, or it expired. */
  cancelPendingOrder: (proposalId: string, reason: string) =>
    invoke<OrderActionResult>('cancel-pending-order', { proposal_id: proposalId, reason }),

  /**
   * Pending order — the user edited price / volume / validity from the Task
   * Dashboard. The row is reset to pending_new so the browser re-validates the
   * new price against the live market before arming it again.
   */
  updatePendingOrder: (proposalId: string, patch: { price: number; volume: number; hours: number }) =>
    invoke<OrderActionResult>('update-pending-order', { proposal_id: proposalId, ...patch }),

  /** Pending order — price touched + task5 risk gate passed: fire a MARKET order. */
  firePendingOrder: (proposalId: string) =>
    invoke<OrderActionResult>('fire-pending-order', { proposal_id: proposalId }),

  /** Pending order — task5 blocked a fire: notify that ALL pending orders are paused. */
  pausePendingAlert: (proposalId: string, reason: string) =>
    invoke<OrderActionResult>('pause-pending-alert', { proposal_id: proposalId, reason }),

  /**
   * task7 — the browser alert engine detected a met condition (price touch /
   * reach MA / break). The edge atomically claims the alert (active → fired, so
   * it fires exactly ONCE, no spam) and sends the notification with
   * [📝 Note] / [🚀 Vào lệnh] buttons. `context` is optional trigger detail used
   * to enrich the message (e.g. the live price, the distance, the A0 close).
   */
  fireAlert: (alertId: string, context?: Record<string, unknown>) =>
    invoke<FireAlertResult>('fire-alert', { alert_id: alertId, context: context ?? null }),
};

// ─── Workflow presets ───────────────────────────────────────────

/**
 * Standard multi-step "entry" approval:
 *   step 1 → choose side (BUY / SELL)
 *   step 2 → choose volume
 *   step 3 → confirm (executes a MARKET order on ✅)
 *
 * Extend or replace the steps later to match the detailed cases you design —
 * the engine is fully driven by this declarative structure.
 */
export function buildEntryApprovalWorkflow(opts?: {
  volumeOptions?: number[];
}): TelegramProposalWorkflow {
  const volumes = opts?.volumeOptions ?? [0.01, 0.05, 0.1, 0.5];
  return {
    final_action: 'place_order',
    steps: [
      {
        id: 'side',
        prompt: 'Chọn hướng vào lệnh cho cặp này:',
        input: 'choice',
        field: 'trade_side',
        options: [
          { label: '🟢 BUY', value: 'BUY' },
          { label: '🔴 SELL', value: 'SELL' },
        ],
      },
      {
        id: 'volume',
        prompt: 'Chọn khối lượng (lots):',
        input: 'choice',
        field: 'volume',
        value_type: 'lots',
        options: volumes.map((v) => ({ label: String(v), value: String(v) })),
      },
      {
        id: 'confirm',
        prompt: 'Xác nhận đặt lệnh MARKET?',
        input: 'confirm',
      },
    ],
  };
}

/**
 * "Close position" approval: pick a position, then confirm the close.
 */
export function buildCloseApprovalWorkflow(positionIds: Array<{ id: number; label: string }>): TelegramProposalWorkflow {
  return {
    final_action: 'close_position',
    steps: [
      {
        id: 'position',
        prompt: 'Chọn vị thế cần đóng:',
        input: 'choice',
        field: 'position_id',
        value_type: 'number',
        options: positionIds.map((p) => ({ label: p.label, value: String(p.id) })),
      },
      { id: 'confirm', prompt: 'Xác nhận đóng vị thế này?', input: 'confirm' },
    ],
  };
}

export default telegramBot;